import { FC, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  styled, 
  MenuItem, 
  Select, 
  Link,
  Typography, 
  SelectChangeEvent} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download';
import { DatabaseResponseDto, DatabaseType } from "@/gen/api/backup";
import { SaveActionSlim } from "@/dataProvider/types";
import { useCallback } from "react";
import { useEffect } from "react";
import { Link as RouterLink } from 'react-router-dom'
import { crudGetOne, useGetList } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import { merge } from "lodash";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_BACKUP_JOBS } from "@/constants";
import { nonSqlDbTypes } from "../obfuscation/defs";


export interface BackupBasicInfoCardProps {
  preChosenDbId: number;
  onSwitchDatabase: SaveActionSlim<DatabaseResponseDto>;
}

export const BasicInfoRow = styled('div')(({ theme }) => ({
  display: 'flex',
  '& > div': {
    padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
    flexGrow: 1,
    '&:first-of-type': {
      borderRight: `1px solid ${theme.palette.divider}`
    },
  }
}))

export const BasicInfoPiece = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  '& > :first-of-type': { flex: '1 1 auto', marginRight: 32 },
  '& > :last-of-type': { flex: '2 2 auto' },
})

const BackupBasicInfoCard: FC<BackupBasicInfoCardProps> = ({
  preChosenDbId,
  onSwitchDatabase,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_BACKUP_JOBS])
  const { data: databasesMap } = useGetList<DatabaseResponseDto & RecPatch>('databases')
  const databases = Object.values(databasesMap || {}).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1)

  const [currDb, setCurrDb] = useState<DatabaseResponseDto>(databasesMap[preChosenDbId] || ('' as const))
  
  const handleChange = useCallback((e: SelectChangeEvent<'' | DatabaseResponseDto>) => {
    if (e.target.value === '') { return }

    const chosenDb = e.target.value as DatabaseResponseDto
    setCurrDb(chosenDb)
    onSwitchDatabase(chosenDb)
  }, [setCurrDb, onSwitchDatabase])
  
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const onDownloadDatabaseConfigFile = () => 
    dispatch(merge(
      crudGetOne('upload-config-file', currDb?.databaseId || 0, '', false),
      {
        onFailure: (err: any) => {
          const errMsg = retrieveAxiosErr(err)
          enqueueSnackbar(`Download failed: ${errMsg}`, { variant: 'error' })
        }
      }
    ))

  useEffect(() => {
    setCurrDb(databasesMap[preChosenDbId] || '')
  }, [preChosenDbId, databasesMap])

  const isNoSql = nonSqlDbTypes.has(currDb.databaseType!)

  return (
    <Card>
      <BasicInfoRow>
        <BasicInfoPiece>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Database
          </Typography>
          <Box>
            <Select
              fullWidth
              name="option"
              onChange={handleChange}
              size="small"
              value={currDb || ''}
              variant="outlined"
            >
              {databases.map(d => (
                <MenuItem key={d.databaseId} value={d as any}>{d.name}</MenuItem>
                ))}
            </Select>
          </Box>
        </BasicInfoPiece>
        <BasicInfoPiece>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Current Engine Version
          </Typography>
          <Typography
            color="textSecondary"
            variant="body1"
          >
            {currDb && (currDb.engineVersion + ' ' + (currDb.engineEdition || ''))}
          </Typography>
        </BasicInfoPiece>
      </BasicInfoRow>
      {currDb && (!currDb.isManaged || currDb.description) && <Divider />}
      {currDb && currDb.description &&
        <>
          <Box sx={{ px: 3, mt: 1, mb: 2 }}>
            <Typography variant="body2">{currDb.description.replace(/^\w/, (m) => m.toUpperCase())}</Typography>
          </Box>
        </>
      }
      {currDb && !currDb.isManaged && !isNoSql && hasWritePermit &&
        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            flexDirection: 'column',
            px: 2,
            my: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{ p: 1, fontStyle: 'italic' }}
          >
            For databases that are not managed by OnData, the database backup is performed outside of the OnData platform and the database backup files are uploaded to OnData. 
            Please use this configuration file for database upload and follow {" "}
            <Link 
              component={RouterLink} 
              variant="caption" 
              underline="always" 
              to={currDb && currDb.databaseType ? docuUrlMap[currDb.databaseType || DatabaseType.SQLServer] : docuUrlMap[DatabaseType.SQLServer]}
            >
              these instructions 
            </Link> {" "}
            to automate the database backup and upload process.
          </Typography>
          <Button
            color="inherit"
            startIcon={<DownloadIcon fontSize="small" />}
            variant="text"
            onClick={onDownloadDatabaseConfigFile}
          >
            Config File for Upload
          </Button>
        </Box>}
    </Card>
    
  )
}

export default BackupBasicInfoCard;

const docuUrlMap = {
  [DatabaseType.MySQL]: '/documents/securedb/database_backup/mysql',
  [DatabaseType.PostgreSQL]: '/documents/securedb/database_backup/postgresql',
  [DatabaseType.SQLServer]: '/documents/securedb/database_backup/microsoft sql server',
  [DatabaseType.MariaDB]: '/documents/securedb/database_backup/mariadb',
  [DatabaseType.Oracle]: '/documents/securedb/database_backup/oracle',
  [DatabaseType.DB2]: '/documents/securedb/database_backup/db2',
  // [DatabaseType.Hana]: '/documents/securedb/database_backup/hana',
  [DatabaseType.Snowflake]: '/documents/securedb/database_backup/snowflake',
} as Record<DatabaseType, string>
